import { useState } from "react";

import Typography from "@/design-system/Typography";
import TelemetryButton, { type IButtonTelemetryProps } from "@/design-system/telemetry-button";
import type { IImageLoader } from "@Components/ImageLoader";
import * as stylex from "@stylexjs/stylex";

import type { IInfoLabel } from "../shared/InfoLabel";
import { InfoLabel } from "../shared/InfoLabel";
import { ComposableSpacing, colors, genericStyles, globalLimitStyles } from "./../../styles/tokens.stylex";

export interface ILayerTile {
    id: number;
    image: IImageLoader;
    title?: string;
    description?: string;
    infoLabel?: IInfoLabel;
    slug: string;
    telemetryCustom?: IButtonTelemetryProps;
}

export default function LayerTile(props: ILayerTile) {
    const [isHovered, setIsHovered] = useState(false);

    return (
        <TelemetryButton
            as="a"
            size="None"
            variant="transparent"
            href={`/blog/${props.slug}/${props.id}`}
            styleX={styles.link}
            isExternal
            {...props.telemetryCustom}
        >
            <div
                {...stylex.props(styles.layerImageWrapper)}
                onMouseEnter={() => setIsHovered(true)}
                onMouseLeave={() => setIsHovered(false)}
            >
                <div {...stylex.props(styles.layerImage)}>
                    <img
                        src={props.image.src}
                        alt={props.image.alt}
                        loading="lazy"
                        {...stylex.props(styles.img, isHovered && styles.imgHover)}
                    />
                    {props.description && (
                        <div
                            {...stylex.props(styles.layerScale, isHovered && styles.layerScaleHover)}
                            dangerouslySetInnerHTML={{ __html: props.description }}
                        />
                    )}
                </div>
                {props.infoLabel && (
                    <InfoLabel {...props.infoLabel} iconType="Star" styleX={styles.infoLabel} />
                )}
            </div>
            {props.title && (
                <Typography
                    fontStyle="BodyL"
                    styleX={[globalLimitStyles.TextTwoLinesCap, styles.tileDescription]}
                >
                    {props.title}
                </Typography>
            )}
        </TelemetryButton>
    );
}

const styles = stylex.create({
    link: {
        padding: 0,
        flex: "0 32%",
        display: "inline-flex",
        flexDirection: "column",
    },
    layerImageWrapper: {
        borderRadius: ComposableSpacing.s2,
        overflow: "hidden",
        backgroundColor: "#e6e6e6",

        position: "relative",
        paddingBottom: "52.5%",
        width: "100%",
    },
    layerImage: {
        borderRadius: ComposableSpacing.s2,

        overflow: "hidden",
        alignItems: "center",
        display: "flex",
        position: "absolute",
        bottom: 0,
        left: 0,
        right: 0,
        top: 0,
    },
    img: {
        transform: "scale(1)",
        transitionDuration: "300ms",
        transitionProperty: "transform",
        transitionTimingFunction: "ease-out",
        willChange: "transform",
    },
    imgHover: {
        transform: "scale(1.2)",
    },
    layerScale: {
        borderRadius: genericStyles.borderRadiusFullRound,
        alignItems: "center",
        backgroundColor: "#00000099",
        color: colors.fg_AltDefault,
        display: "flex",
        fontSize: "0.9em",
        justifyContent: "center",
        position: "absolute",
        textAlign: "center",
        transform: "scale(0)",
        transitionDuration: "300ms",
        height: "100%",
        transitionProperty: "transform",
        paddingLeft: "5%",
        paddingRight: "5%",
        transitionTimingFunction: "ease-out",
        width: "100%",
    },
    layerScaleHover: {
        borderRadius: 0,
        transform: "scale(1.1)",
    },

    infoLabel: {
        position: "absolute",
        right: "4%",
        top: "5%",
    },
    tileDescription: {
        fontWeight: "bold",
        textAlign: "left",
    },
});
